// frontend/modules/template/components/TemplateImportExport.tsx
import React, { useRef, useState } from 'react';
import { Button, Upload, Modal, Space, Typography, Alert } from 'antd';
import { UploadOutlined, DownloadOutlined, QuestionCircleOutlined, FileTextOutlined } from '@ant-design/icons';
import type { UploadProps } from 'antd';
import { useTheme } from '@/components/common/ThemeProvider';

const { Title, Text, Paragraph } = Typography;

interface TemplateImportExportProps {
    onExport: () => void;
    onImport: (file: File) => Promise<void>;
    isLoading: boolean;
}

const TemplateImportExport: React.FC<TemplateImportExportProps> = ({
                                                                       onExport,
                                                                       onImport,
                                                                       isLoading
                                                                   }) => {
    const { dark } = useTheme();
    const [isImportModalVisible, setIsImportModalVisible] = useState(false);
    const [isHelpModalVisible, setIsHelpModalVisible] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleImportClick = () => {
        setIsImportModalVisible(true);
    };

    const handleImportConfirm = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            await onImport(file);
            setIsImportModalVisible(false);

            // Reset file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const showHelpModal = () => {
        setIsHelpModalVisible(true);
    };

    return (
        <div>
            <Space>
                <Button
                    onClick={onExport}
                    icon={<DownloadOutlined />}
                    loading={isLoading}
                >
                    Export Templates
                </Button>
                <Button
                    onClick={handleImportClick}
                    icon={<UploadOutlined />}
                    loading={isLoading}
                >
                    Import Templates
                </Button>
                <Button
                    type="link"
                    onClick={showHelpModal}
                    icon={<QuestionCircleOutlined />}
                >
                    Help
                </Button>
            </Space>

            {/* Hidden file input for import */}
            <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                accept=".json"
                onChange={handleFileChange}
            />

            {/* Import Confirmation Modal */}
            <Modal
                title="Import Templates"
                open={isImportModalVisible}
                onOk={handleImportConfirm}
                onCancel={() => setIsImportModalVisible(false)}
                okText="Select File"
                className={dark ? 'dark' : ''}
            >
                <Alert
                    message="Warning"
                    description="Importing templates may overwrite existing templates with the same name and platform. Make sure you have exported your existing templates as a backup."
                    type="warning"
                    showIcon
                    className="mb-4"
                />
                <Paragraph>
                    Select a JSON file containing templates to import. The file should be in the same format as the exported templates.
                </Paragraph>
            </Modal>

            {/* Help Modal */}
            <Modal
                title="Import/Export Help"
                open={isHelpModalVisible}
                onOk={() => setIsHelpModalVisible(false)}
                onCancel={() => setIsHelpModalVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setIsHelpModalVisible(false)}>
                        Close
                    </Button>
                ]}
                width={600}
                className={dark ? 'dark' : ''}
            >
                <div className="space-y-4">
                    <div>
                        <Title level={5}>Export Templates</Title>
                        <Paragraph>
                            The export function creates a JSON file containing all your templates. Use this to:
                        </Paragraph>
                        <ul className="list-disc pl-5 mb-4">
                            <li>Back up your templates</li>
                            <li>Transfer templates between environments</li>
                            <li>Share templates with other teams</li>
                        </ul>
                    </div>

                    <div>
                        <Title level={5}>Import Templates</Title>
                        <Paragraph>
                            The import function allows you to upload a JSON file containing templates. The file should have the same structure as an exported file.
                        </Paragraph>
                        <Alert
                            message="Note"
                            description="When importing, templates with the same name and platform will be overwritten."
                            type="info"
                            showIcon
                            className="mb-4"
                        />
                    </div>

                    <div>
                        <Title level={5}>JSON Format</Title>
                        <Paragraph>
                            The JSON file should contain an array of template objects, each with the following structure:
                        </Paragraph>
                        <pre className={`p-3 rounded overflow-x-auto text-sm ${dark ? 'bg-gray-800 text-gray-200' : 'bg-gray-100'}`}>
{`[
  {
    "name": "template_name",
    "language": "EN",
    "platform": "WHATS_APP",
    "contentType": "PLAIN",
    "content": "Hello {{name}}, your balance is {{amount}}."
  },
  {
    "name": "template_name",
    "language": "SW",
    "platform": "WHATS_APP",
    "contentType": "PLAIN",
    "content": "Hujambo {{name}}, salio lako ni {{amount}}."
  }
]`}
            </pre>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default TemplateImportExport;